/**
 * useAdminUsers.ts — MER Algo Footprints
 *
 * Fetch + mutation hook for /api/admin/users.
 *
 * Access control: backend enforces role='admin'; route-level gating keeps
 * the page hidden from non-admins so this hook never runs for them.
 *
 * States:
 *   loading  — initial fetch in progress
 *   ready    — data loaded successfully
 *   error    — fetch failed or backend returned non-ok response
 *
 * Mutations optimistically update local state. On rejection, the hook
 * calls reload() to resync with the source of truth. Callers receive
 * { ok, reason } from each mutation so they can surface errors.
 */

import { useCallback, useEffect, useState } from 'react';

export interface AdminUserRow {
  id:          number;
  email:       string;
  role:        'user' | 'admin';
  tier:        'core' | 'byob' | 'premium';
  isActive:    boolean;
  createdAt:   string;
  updatedAt:   string;
  lastLoginAt: string | null;
}

export type AdminUsersStatus = 'loading' | 'ready' | 'error';

export interface MutationResult {
  ok:     boolean;
  reason?: string;
}

export interface UseAdminUsersResult {
  status:     AdminUsersStatus;
  users:      AdminUserRow[];
  error:      string | null;
  reload:     () => void;
  setActive:  (id: number, active: boolean) => Promise<MutationResult>;
  setRole:    (id: number, role: 'admin' | 'user') => Promise<MutationResult>;
}

export function useAdminUsers(): UseAdminUsersResult {
  const [status, setStatus] = useState<AdminUsersStatus>('loading');
  const [users,  setUsers]  = useState<AdminUserRow[]>([]);
  const [error,  setError]  = useState<string | null>(null);
  const [nonce,  setNonce]  = useState(0);

  const reload = useCallback(() => setNonce(n => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatus('loading');
      setError(null);
      try {
        const r    = await fetch('/api/admin/users', { credentials: 'include' });
        const data = await r.json();
        if (cancelled) return;
        if (!r.ok || !data.ok) {
          setStatus('error');
          setError(data?.reason ?? `HTTP_${r.status}`);
          return;
        }
        setUsers(Array.isArray(data.users) ? data.users : []);
        setStatus('ready');
      } catch {
        if (cancelled) return;
        setStatus('error');
        setError('NETWORK_ERROR');
      }
    })();
    return () => { cancelled = true; };
  }, [nonce]);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const patch = useCallback(async (
    url:  string,
    body: unknown,
  ): Promise<{ ok: boolean; reason?: string; user?: AdminUserRow }> => {
    try {
      const r = await fetch(url, {
        method:      'PATCH',
        credentials: 'include',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok || !data.ok) {
        return { ok: false, reason: data?.reason ?? `HTTP_${r.status}` };
      }
      return { ok: true, user: data.user as AdminUserRow };
    } catch {
      return { ok: false, reason: 'NETWORK_ERROR' };
    }
  }, []);

  const setActive = useCallback(async (id: number, active: boolean): Promise<MutationResult> => {
    const result = await patch(`/api/admin/users/${id}/active`, { active });
    if (result.ok && result.user) {
      // Optimistically merge the updated row into local state
      setUsers(curr => curr.map(u => u.id === id ? result.user! : u));
      return { ok: true };
    }
    // Failure: reload to ensure local state matches server
    reload();
    return { ok: false, reason: result.reason };
  }, [patch, reload]);

  const setRole = useCallback(async (id: number, role: 'admin' | 'user'): Promise<MutationResult> => {
    const result = await patch(`/api/admin/users/${id}/role`, { role });
    if (result.ok && result.user) {
      setUsers(curr => curr.map(u => u.id === id ? result.user! : u));
      return { ok: true };
    }
    reload();
    return { ok: false, reason: result.reason };
  }, [patch, reload]);

  return { status, users, error, reload, setActive, setRole };
}
